/**
 * There is code in this file to route communication with Adobe apps through IFrame postmessage API when Mimir is
 * loaded in an IFrame.
 * Those can be removed when we can make sure that all customers have upgraded to panel version 3.1.0 or later,
 * which loads the panel in the main window of the panel and does not need an IFrame.
 */

import axios from 'axios';
import { reconstitutedException } from '@mimir/common/util/exception';
import { routeViaParentFrame } from './cepEnvironment';
import type { NleEventBody } from '@mimir/primitive/nle/event';

// ─────────────────────────────────────────
// Transport layer
//
// Two implementations selected at call time (not at init) because
// isInTopFrame() can change after an AD login redirect.
// ─────────────────────────────────────────

type CepInterFrameMessageType =
  | 'EVAL_SCRIPT_REQUEST'
  | 'EVAL_SCRIPT_RESPONSE'
  | 'EVENT_SUBCRIPTION_REQUEST'
  | 'EVENT_DATA';

interface CepInterFrameMessage {
  messageId: string;
  payload: string | LowLevelCEPEvent;
  type: CepInterFrameMessageType;
}
interface LowLevelCEPEvent {
  type: string;
  scope: 'APPLICATION' | 'GLOBAL' | string;
  appId: string;
  extensionId: string;
  data: NleEventBody;
}
interface CepTransport {
  evalScript(script: string, callback: (result: string) => void): void;
  addEventListener(type: string, listener: (msg: NleEventBody) => void): void;
}

// CEP < 11, or top frame after AD login redirect
const directTransport: CepTransport = {
  evalScript(script, callback) {
    window.__adobe_cep__.evalScript(script, callback);
  },
  addEventListener(type, listener) {
    window.__adobe_cep__.addEventListener(type, (event: LowLevelCEPEvent) => listener(event.data), undefined);
  },
};

// CEP >= 11 in child frame: all state encapsulated in this closure
const iframeTransport: CepTransport = (() => {
  let counter = Date.now();
  const cbMap = new Map<string, (result: string) => void>();
  const cbEventsMap = new Map<string, (result: LowLevelCEPEvent) => void>();

  function handleMessage(event: MessageEvent) {
    const message = event.data as CepInterFrameMessage;
    if (!message?.messageId) {
      console.warn(`Skipping unrecognized message: ${message}`);
      return;
    }
    if (message.type === 'EVENT_DATA' && typeof message.payload === 'object') {
      const callback = cbEventsMap.get(message.messageId);
      if (!callback) {
        console.warn(`Skipping event message without callback: ${message.messageId}`);
        return;
      }
      callback(message.payload);
    } else if (message.type === 'EVAL_SCRIPT_RESPONSE' && typeof message.payload === 'string') {
      const callback = cbMap.get(message.messageId);
      if (!callback) {
        console.warn(`Skipping message without callback: ${message.messageId}`);
        return;
      }
      cbMap.delete(message.messageId);
      callback(message.payload);
    }
  }

  window.addEventListener('message', handleMessage);

  function post(type: CepInterFrameMessageType, payload: string): string {
    const messageId = `${counter++}`;
    window.parent.postMessage({ messageId, payload, type }, '*');
    return messageId;
  }

  return {
    evalScript(script, callback) {
      cbMap.set(post('EVAL_SCRIPT_REQUEST', script), callback);
    },
    addEventListener(type, listener) {
      cbEventsMap.set(post('EVENT_SUBCRIPTION_REQUEST', type), event => listener(event.data));
    },
  };
})();

function getTransport(): CepTransport {
  return routeViaParentFrame() ? iframeTransport : directTransport;
}

// ─────────────────────────────────────────
// Raw script execution
// ─────────────────────────────────────────

export const addEventListener = (type: string, listener: (msg: NleEventBody) => void): void => {
  getTransport().addEventListener(type, listener);
};

export const evalRawScript = (script: string): Promise<string> =>
  new Promise((resolve) => {
    getTransport().evalScript(script, (result) => {
      console.debug(`evalRawScript result: ${result}`);
      resolve(result);
    });
});

// Calls a method on a loaded Adobe host bundle (ae / ps / audition).
// The bundle must be loaded first via loadAppBundle() in initialization.ts,
// which assigns its exports to the global MimirPanel.
export async function evalHostMethod(method: string, args: unknown[]): Promise<unknown> {
  const argsJson = JSON.stringify(JSON.stringify(args));
  const callScript = `
    var __r;
    try {
      __r = JSON.stringify({ result: MimirPanel['${method}'].apply(null, JSON.parse(${argsJson})) });
    } catch (e) {
      __r = JSON.stringify({ error: { message: String(e.message || e), name: String(e.name || '') } });
    }
    __r;
  `;
  const reply = await evalRawScript(callScript);
  const obj = JSON.parse(reply) as { result?: unknown; error?: unknown };
  if (obj.error !== undefined) {
    throw reconstitutedException(obj.error);
  }
  return obj.result;
}

// ─────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────

export const openURLInDefaultBrowser = (url: string) => {
  window.cep.util.openURLInDefaultBrowser(url);
};

export async function knowsAboutAME(): Promise<boolean> {
  const result = await new Promise((resolve) => {
    window.__adobe_cep__.evalScript('app.encoder', resolve);
  });
  return result === '[object Encoder]';
}

export async function getFresh(url: string) {
  const timestamp = new Date().getTime();
  const response = await axios.get(`${url}?ts=${timestamp}`);
  return response.data;
}

